import { DateTime24 } from "./DateTime24";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { AlertTriangle, Link2, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { createJob, checkRegoInYard, checkRegoInImpound, getYardLinkPreview, confirmYardLink } from "../lib/api";
import { SERVICE_TYPES, PRIORITY_OPTIONS, DEFAULT_CENTER } from "../lib/constants";
import AddressAutocomplete from "./AddressAutocomplete";
import { PinDropInput } from "./PinDropInput";
import { phonePlaceholder, salvageFull, salvageShort, tenantLocale, tenantTz } from "../lib/company";
import { wallToDate, wallToIso } from "../lib/tenantTime";

const empty = {
  customer_name: "",
  customer_phone: "",
  address: "",
  lat: null,
  lng: null,
  drop_off_address: "",
  drop_off_lat: null,
  drop_off_lng: null,
  vehicle: "",
  rego: "",
  claim_number: "",
  tow_authority_number: "",
  service_type: "tow",
  priority: "normal",
  notes: "",
  source: "manual",
};

export default function NewJobSheet({ open, onOpenChange, onCreated, prefill }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  // Hydrate from `prefill` ONCE on open — a dispatcher opening the sheet
  // from Tow Calc's "Create as Job" button gets pickup, drop-off, the
  // quoted manual charge and the heavy-vehicle flag populated so they
  // only need to fill customer / phone / rego / vehicle. Guarded by a
  // ref-like flag so subsequent typing isn't overwritten on re-renders.
  useEffect(() => {
    if (!open || !prefill) return;
    setForm((f) => ({
      ...empty,
      // Keep whatever the user already typed if they opened again mid-session.
      ...f,
      ...(prefill.address ? { address: prefill.address, lat: prefill.lat ?? null, lng: prefill.lng ?? null } : {}),
      ...(prefill.drop_off_address ? {
        drop_off_address: prefill.drop_off_address,
        drop_off_lat: prefill.drop_off_lat ?? null,
        drop_off_lng: prefill.drop_off_lng ?? null,
      } : {}),
      ...(prefill.manual_tow_charge != null ? { manual_tow_charge: prefill.manual_tow_charge } : {}),
      ...(prefill.heavy_vehicle != null ? { heavy_vehicle: !!prefill.heavy_vehicle } : {}),
      // Feb 2026 — Tow Calc stamps the quoted price + basis into Notes
      // so the dispatcher has an audit record of what we quoted the
      // customer, even if the manual charge is later overridden.
      // We prepend to any existing Notes rather than overwrite so a
      // dispatcher who typed before opening Tow Calc isn't blown away.
      ...(prefill.notes ? { notes: f.notes ? `${prefill.notes}\n\n${f.notes}` : prefill.notes } : {}),
    }));
    // Depend on `open` so prefill is re-applied each time the sheet opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Schedule modes:
  //   "now"       → straight to pending queue
  //   "later"     → held in Tow Booked until the booked day
  //   "completed" → retroactive entry for jobs done outside dispatcher hours
  //                 (Feb 2026 — captures storage clock for overnight/weekend
  //                 jobs entered the next morning)
  const [when, setWhen] = useState("now");
  const [bookedFor, setBookedFor] = useState("");
  const [completedAt, setCompletedAt] = useState("");
  // Live yard-guard: rego already sitting in one of our yards with a
  // running storage clock → block creation, point at On-tow out.
  const [yardMatch, setYardMatch] = useState(null);
  // Live impound-guard (Feb 2026): rego on an active police impound →
  // block creation, point at the existing impound record.
  const [impoundMatch, setImpoundMatch] = useState(null);
  // Yard-outbound auto-link preview (Feb 2026) — after a RETRO manual
  // entry (`when === "completed"`) whose rego matches a yarded vehicle,
  // the backend suggests linking it as a release. We hold the created
  // job + suggested match here so the confirmation dialog can render.
  const [pendingYardLink, setPendingYardLink] = useState(null);
  // Duplicate-job guard (Feb 2026 — Jeremy's phoned-in overnight + driver-
  // relogged next morning case). When the server returns 409/duplicate_job,
  // stash the existing job here and render the confirm modal below.
  const [duplicateExisting, setDuplicateExisting] = useState(null);
  const [forceCreate, setForceCreate] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Clear all form state back to blank. Called ONLY by the explicit
  // Cancel button (below). Closing via click-outside / Esc / X leaves
  // the form intact so a dispatcher who tapped away by accident (or to
  // check something) can pick up where they left off. Reported Feb 2026:
  // dispatcher was mid-entry, realised the rego was actually an impound,
  // hit Cancel — then the NEXT new-job attempt still had the old details.
  const resetForm = () => {
    setForm(empty);
    setWhen("now");
    setBookedFor("");
    setCompletedAt("");
    setForceCreate(false);
    setYardMatch(null);
    setImpoundMatch(null);
  };

  const handleCancelClick = () => {
    resetForm();
    onOpenChange(false);
  };

  useEffect(() => {
    const rego = (form.rego || "").trim();
    if (!open || rego.length < 3) {
      setYardMatch(null);
      setImpoundMatch(null);
      return;
    }
    const t = setTimeout(() => {
      checkRegoInYard(rego)
        .then((r) => setYardMatch(r.in_yard ? r : null))
        .catch(() => setYardMatch(null));
      checkRegoInImpound(rego)
        .then((r) => setImpoundMatch(r.in_impound ? r : null))
        .catch(() => setImpoundMatch(null));
    }, 400);
    return () => clearTimeout(t);
  }, [open, form.rego]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.address || !form.vehicle) {
      toast.error("Please fill customer, pickup address, and vehicle.");
      return;
    }
    if (yardMatch && when !== "completed") {
      // Live job (Now / Book for later) for a yarded vehicle: dispatcher
      // should use On-tow out so the outbound is created cleanly. Retro
      // entries fall through — the auto-linker handles them post-create
      // via the confirm dialog below.
      toast.error(`${yardMatch.rego} is already in storage at ${yardMatch.yard_label} — use On-tow out instead.`);
      return;
    }
    if (impoundMatch) {
      toast.error(`${impoundMatch.rego} is on an active police impound — release the impound (or edit the existing record) first.`);
      return;
    }
    if (when === "later") {
      if (!bookedFor) {
        toast.error("Pick the booked date & time.");
        return;
      }
      if (wallToDate(bookedFor) <= new Date()) {
        toast.error("Booked time must be in the future.");
        return;
      }
    }
    if (when === "completed") {
      if (!completedAt) {
        toast.error("Pick when this job was actually completed.");
        return;
      }
      if (wallToDate(completedAt) > new Date()) {
        toast.error("Completed time must be in the past.");
        return;
      }
    }
    setSaving(true);
    try {
      // If user typed freely without picking a suggestion, the backend will geocode it.
      // If they DID pick, lat/lng/drop_off_lat/drop_off_lng come along for the ride.
      const payload = {
        ...form,
        lat: form.lat ?? DEFAULT_CENTER[0],
        lng: form.lng ?? DEFAULT_CENTER[1],
        ...(when === "later" ? { booked_for: wallToIso(bookedFor) } : {}),
        ...(when === "completed" ? { completed_at: wallToIso(completedAt) } : {}),
      };
      const created = await createJob(payload, { force: forceCreate });
      toast.success(
        when === "later"
          ? `Tow booked for ${wallToDate(bookedFor).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" })}`
          : when === "completed"
          ? `Retroactive job logged as completed ${wallToDate(completedAt).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" })}`
          : `Job created for ${created.customer_name}`
      );

      // Retro yard-outbound auto-link (Feb 2026): ONLY on `completed`
      // entries do we prompt the operator to link this job as a
      // release from one of our yards. The backend already ran the
      // linker check for AA/FR/email sources at insert time; the
      // manual retro flow requires a confirm modal so the operator can
      // verify the pairing before writing the link.
      if (when === "completed") {
        let preview = null;
        try {
          const p = await getYardLinkPreview(created.id);
          preview = p?.possible_yard_link || null;
        } catch { /* silent — no preview endpoint failure should block create */ }
        if (preview) {
          setPendingYardLink({ job: created, match: preview });
          // Hold the sheet open so the confirm dialog anchors correctly.
          // Reset form now so a "Skip" cleanly closes to a blank state.
          setForm(empty);
          setWhen("now");
          setBookedFor("");
          setCompletedAt("");
          onCreated?.(created);
          window.dispatchEvent(new Event("jobs:refresh"));
          return;
        }
      }

      setForm(empty);
      setWhen("now");
      setBookedFor("");
      setCompletedAt("");
      setForceCreate(false);
      onOpenChange(false);
      onCreated?.(created);
      window.dispatchEvent(new Event("jobs:refresh"));
    } catch (err) {
      // Duplicate-job guard (Feb 2026): backend returns 409 with a
      // `duplicate_job` code + the existing job's basics when rego +
      // vehicle + pickup + drop-off all match. Surface it as a confirm
      // modal so the operator can open the existing job or override.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && typeof detail === "object" && detail?.code === "duplicate_job") {
        setDuplicateExisting(detail.existing);
        setSaving(false);
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Failed to create job");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        data-testid="new-job-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl font-black tracking-tight">
            New Job Intake
          </SheetTitle>
          <SheetDescription>
            Log a new roadside rescue request. It will appear in the queue immediately.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="new-job-form">
          <div>
            <Label className="uppercase-label">Schedule</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              <Button
                type="button"
                size="sm"
                variant={when === "now" ? "default" : "outline"}
                className={when === "now" ? "bg-slate-900 hover:bg-slate-800 text-white border border-slate-900 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 dark:border-slate-100 dark:hover:border-slate-100 shadow-sm ring-2 ring-slate-900/20 dark:ring-slate-100/30" : ""}
                onClick={() => setWhen("now")}
                data-testid="job-schedule-now"
              >
                Now
              </Button>
              <Button
                type="button"
                size="sm"
                variant={when === "later" ? "default" : "outline"}
                className={when === "later" ? "bg-slate-900 hover:bg-slate-800 text-white border border-slate-900 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 dark:border-slate-100 dark:hover:border-slate-100 shadow-sm ring-2 ring-slate-900/20 dark:ring-slate-100/30" : ""}
                onClick={() => setWhen("later")}
                data-testid="job-schedule-later"
              >
                Book for later
              </Button>
              <Button
                type="button"
                size="sm"
                variant={when === "completed" ? "default" : "outline"}
                className={when === "completed" ? "bg-slate-900 hover:bg-slate-800 text-white border border-slate-900 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 dark:border-slate-100 dark:hover:border-slate-100 shadow-sm ring-2 ring-slate-900/20 dark:ring-slate-100/30" : ""}
                onClick={() => setWhen("completed")}
                data-testid="job-schedule-completed"
              >
                Already completed
              </Button>
            </div>
            {when === "later" && (
              <div className="mt-2">
                <DateTime24
                  value={bookedFor}
                  onChange={setBookedFor}
                  dataTestId="job-booked-for-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Held in the <b>Tow Booked</b> tab — auto-releases into the queue at 06:00 on the
                  booked day (same-day bookings release at the booked time). You can also release it manually any time.
                </p>
              </div>
            )}
            {when === "completed" && (
              <div className="mt-2">
                <DateTime24
                  value={completedAt}
                  onChange={setCompletedAt}
                  dataTestId="job-completed-at-input"
                />
                <div className="mt-2">
                  <Label className="uppercase-label">Tow Authority Number <span className="text-slate-400 font-normal normal-case">(optional)</span></Label>
                  <Input
                    value={form.tow_authority_number}
                    onChange={(e) => upd("tow_authority_number", e.target.value)}
                    placeholder="e.g. TA0042"
                    data-testid="job-input-tow-authority-number"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Aug 2026 — surfaced on retro entries because after-hours / weekend jobs
                    are often loaded with the TA number already in hand. Editable later via
                    the History table if left blank.
                  </p>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Retroactive entry — logs the job straight to <b>Completed</b> anchored at the
                  chosen time (any past date). If the drop-off is a yard, the storage clock starts
                  at the next NZ midnight after this time so overnight/weekend jobs bill correctly.
                  <span className="block mt-0.5 text-[11px] text-slate-400">
                    Silent create — no driver notification.
                  </span>
                </p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="uppercase-label">Customer Name</Label>
              <Input
                value={form.customer_name}
                onChange={(e) => upd("customer_name", e.target.value)}
                placeholder="John Smith"
                data-testid="job-input-customer-name"
              />
            </div>

            <div>
              <Label className="uppercase-label">Phone</Label>
              <Input
                value={form.customer_phone}
                onChange={(e) => upd("customer_phone", e.target.value)}
                placeholder={phonePlaceholder()}
                data-testid="job-input-customer-phone"
              />
            </div>

            <div>
              <Label className="uppercase-label">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => upd("priority", v)}>
                <SelectTrigger data-testid="job-select-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`priority-opt-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label className="uppercase-label">Pickup Address</Label>
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => {
                  // typed freely → coords stale
                  setForm((f) => ({ ...f, address: v, lat: null, lng: null }));
                }}
                onPick={({ label, lat, lng }) =>
                  setForm((f) => ({ ...f, address: label, lat, lng }))
                }
                placeholder="Start typing an address…"
                testIdPrefix="job-input-address"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Pick a suggestion for the most reliable coordinates. Free-typed addresses are geocoded on save.
              </p>
              <PinDropInput
                testIdPrefix="job-pin-drop"
                onResolved={({ address, lat, lng }) =>
                  setForm((f) => ({ ...f, address, lat, lng }))
                }
              />
            </div>

            <div className="col-span-2">
              <Label className="uppercase-label">Drop-off Address (Optional)</Label>
              <AddressAutocomplete
                value={form.drop_off_address}
                onChange={(v) => {
                  setForm((f) => ({ ...f, drop_off_address: v, drop_off_lat: null, drop_off_lng: null }));
                }}
                onPick={({ label, lat, lng }) =>
                  setForm((f) => ({ ...f, drop_off_address: label, drop_off_lat: lat, drop_off_lng: lng }))
                }
                placeholder="Repair shop / depot / customer destination"
                testIdPrefix="job-input-dropoff"
              />
            </div>

            <div>
              <Label className="uppercase-label">Vehicle</Label>
              <Input
                value={form.vehicle}
                onChange={(e) => upd("vehicle", e.target.value)}
                placeholder="2020 Honda Accord"
                data-testid="job-input-vehicle"
              />
            </div>

            <div>
              <Label className="uppercase-label">Rego / Plate</Label>
              <Input
                value={form.rego}
                onChange={(e) => upd("rego", e.target.value.toUpperCase())}
                placeholder="ABC123"
                className="font-mono uppercase"
                data-testid="job-input-rego"
              />
            </div>

            {yardMatch && when !== "completed" && (
              <div
                className="col-span-2 rounded-md border-2 border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-3 flex items-start gap-2"
                data-testid="rego-in-yard-warning"
              >
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900 dark:text-amber-100">
                  <div className="font-bold">
                    {yardMatch.rego} is currently in storage at {yardMatch.yard_label}
                  </div>
                  <div className="mt-0.5">
                    {yardMatch.vehicle ? `${yardMatch.vehicle} · ` : ""}stored since{" "}
                    {yardMatch.storage_started_at
                      ? new Date(yardMatch.storage_started_at).toLocaleDateString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium" })
                      : "—"}
                    . Use <b>&ldquo;On-tow out&rdquo;</b> on the <b>In {salvageShort()} Yards</b> page instead — that
                    stops the storage clock and links the outbound job for invoicing.
                  </div>
                </div>
              </div>
            )}

            {yardMatch && when === "completed" && (
              <div
                className="col-span-2 rounded-md border-2 border-indigo-400 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-950/30 p-3 flex items-start gap-2"
                data-testid="rego-yard-release-hint"
              >
                <Link2 className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
                <div className="text-xs text-indigo-900 dark:text-indigo-100">
                  <div className="font-bold">
                    {yardMatch.rego} is already in storage at {yardMatch.yard_label}
                  </div>
                  <div className="mt-0.5">
                    {yardMatch.vehicle ? `${yardMatch.vehicle} · ` : ""}stored since{" "}
                    {yardMatch.storage_started_at
                      ? new Date(yardMatch.storage_started_at).toLocaleDateString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium" })
                      : "—"}
                    . If this job is the tow <b>out</b> of the yard, we&apos;ll offer to link it as the release after you
                    save. If it&apos;s the tow <b>in</b>, the car is already on record — this would be a duplicate and won&apos;t save.
                  </div>
                </div>
              </div>
            )}

            {impoundMatch && (
              <div
                className="col-span-2 rounded-md border-2 border-rose-400 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/30 p-3 flex items-start gap-2"
                data-testid="rego-in-impound-warning"
              >
                <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                <div className="text-xs text-rose-900 dark:text-rose-100">
                  <div className="font-bold">
                    {impoundMatch.rego} is on an active police impound
                    {impoundMatch.notice_number ? ` (notice ${impoundMatch.notice_number})` : ""}
                  </div>
                  <div className="mt-0.5">
                    {impoundMatch.vehicle ? `${impoundMatch.vehicle} · ` : ""}
                    {impoundMatch.impounded_at
                      ? `impounded ${new Date(impoundMatch.impounded_at).toLocaleDateString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium" })}`
                      : ""}
                    {impoundMatch.impound_term ? ` · ${impoundMatch.impound_term} term` : ""}.
                    Release the impound (or edit the existing record) on the <b>Impounds</b> page
                    before creating a new tow job for this vehicle — this prevents double-billing.
                  </div>
                </div>
              </div>
            )}

            <div className="col-span-2">
              <Label className="uppercase-label">Insurance Claim # <span className="text-slate-400 normal-case font-normal">(optional)</span></Label>
              <Input
                value={form.claim_number}
                onChange={(e) => upd("claim_number", e.target.value)}
                placeholder="e.g. CL-123456"
                className="font-mono"
                data-testid="job-input-claim-number"
              />
            </div>

            <div className="col-span-2">
              <Label className="uppercase-label">Service Type</Label>
              <Select value={form.service_type} onValueChange={(v) => upd("service_type", v)}>
                <SelectTrigger data-testid="job-select-service">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((s) => (
                    <SelectItem key={s.value} value={s.value} data-testid={`service-opt-${s.value}`}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label className="uppercase-label">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => upd("notes", e.target.value)}
                placeholder="Anything the driver should know..."
                rows={3}
                data-testid="job-input-notes"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCancelClick}
              data-testid="job-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || (!!yardMatch && when !== "completed") || !!impoundMatch}
              className="flex-1 bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
              data-testid="job-submit-btn"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 animate-spin" />Creating…</>
              ) : when === "completed" ? (
                "Log Completed Job"
              ) : when === "later" ? (
                "Book Job"
              ) : (
                "Create Job"
              )}
            </Button>
          </div>
        </form>
      </SheetContent>
      <YardLinkConfirmDialog
        pending={pendingYardLink}
        onClose={(linked) => {
          setPendingYardLink(null);
          onOpenChange(false);
          if (linked) window.dispatchEvent(new Event("jobs:refresh"));
        }}
      />
      <DuplicateJobDialog
        existing={duplicateExisting}
        onClose={() => setDuplicateExisting(null)}
        onCreateAnyway={() => {
          setDuplicateExisting(null);
          setForceCreate(true);
          // Re-fire submit on next tick so React commits forceCreate first.
          setTimeout(() => {
            document.querySelector("form[data-testid='new-job-form']")?.requestSubmit?.();
          }, 0);
        }}
      />
    </Sheet>
  );
}

function DuplicateJobDialog({ existing, onClose, onCreateAnyway }) {
  const nav = useNavigate();
  if (!existing) return null;
  const openExisting = () => {
    onClose();
    nav(`/history?q=${encodeURIComponent(existing.customer_name || "")}&open=${existing.id}`);
  };
  return (
    <AlertDialog open={!!existing} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent data-testid="duplicate-job-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>This looks like a duplicate</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>An existing job matches every field (rego, vehicle, pickup, drop-off).</p>
              <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-md px-3 py-2 text-sm">
                <div className="font-semibold text-slate-800 dark:text-slate-100">
                  {existing.customer_name || "—"}
                </div>
                <div className="text-slate-500 text-xs">
                  Job {existing.id?.slice(0, 8)} · {existing.status}
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel data-testid="duplicate-job-cancel-btn">Cancel</AlertDialogCancel>
          <Button
            variant="outline"
            onClick={openExisting}
            data-testid="duplicate-job-open-btn"
          >
            Open Existing
          </Button>
          <AlertDialogAction
            onClick={onCreateAnyway}
            data-testid="duplicate-job-create-new-btn"
          >
            Create New
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// -----------------------------------------------------------------------------
// Retro yard-release confirm dialog (Feb 2026)
// -----------------------------------------------------------------------------
// After a retroactive job is created for a rego that IS currently in one of
// our yards, the backend suggests linking the two records. We surface a
// small confirm dialog so the operator can OK the pairing (or skip it if
// the job they just entered was for something else entirely).
function YardLinkConfirmDialog({ pending, onClose }) {
  const [busy, setBusy] = useState(false);
  if (!pending) return null;
  const { job, match } = pending;
  const doLink = async () => {
    setBusy(true);
    try {
      await confirmYardLink(job.id);
      toast.success(`Linked to ${match.yarded_rego} — storage will stop when your truck arrives on-site.`);
      onClose(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not link — please try again from the job detail sheet.");
      onClose(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={!!pending} onOpenChange={() => !busy && onClose(false)}>
      <SheetContent side="right" className="w-full sm:max-w-md" data-testid="yard-link-confirm-dialog">
        <SheetHeader>
          <SheetTitle className="font-display text-xl font-black tracking-tight">
            Link as yard release?
          </SheetTitle>
          <SheetDescription>
            The rego you just entered matches a vehicle currently in one of your yards.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-5 rounded-lg border-2 border-indigo-300 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/40 p-4">
          <div className="flex items-start gap-3">
            <Link2 className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
            <div className="text-sm text-indigo-900 dark:text-indigo-100 space-y-1">
              <div className="font-bold font-mono text-base">
                {match.yarded_rego}
              </div>
              {match.vehicle && <div>{match.vehicle}</div>}
              <div>Yard: <b>{match.yard_label}</b></div>
              {match.customer_name && <div>Customer: {match.customer_name}</div>}
              {match.storage_started_at && (
                <div>
                  Stored since{" "}
                  {new Date(match.storage_started_at).toLocaleDateString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium" })}
                </div>
              )}
            </div>
          </div>
        </div>
        <p className="mt-4 text-xs text-slate-500 leading-relaxed">
          If you link, this new job will be marked as the outbound release
          and the storage clock will stop the moment your truck reaches on-site
          at the yard. If they&apos;re actually unrelated, tap <b>Skip</b> and the
          new job stays independent.
        </p>
        <div className="flex gap-2 mt-6">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => onClose(false)}
            disabled={busy}
            data-testid="yard-link-skip-btn"
          >
            Skip — unrelated
          </Button>
          <Button
            type="button"
            className="flex-1 bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            onClick={doLink}
            disabled={busy}
            data-testid="yard-link-confirm-btn"
          >
            {busy ? "Linking..." : "Link as release"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
